import React, { useState, useEffect } from "react";
import { axiosInstance } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { ThumbsUp, MessageSquare } from "lucide-react";

const Blog = ({ user }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentContent, setCommentContent] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    content: ""
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axiosInstance.get("/blog");
      setPosts(response.data);
    } catch (error) {
      toast.error("Failed to load articles");
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (postId) => {
    try {
      const response = await axiosInstance.get(`/blog/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      toast.error("Failed to load comments");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axiosInstance.post("/blog", formData);
      toast.success("Article published successfully!");
      setCreateOpen(false);
      setFormData({ title: "", content: "" });
      fetchPosts();
    } catch (error) {
      toast.error("Failed to publish article");
    }
  };

  const handleVote = async (postId, e) => {
    e.stopPropagation();
    try {
      await axiosInstance.post(`/blog/${postId}/vote`);
      fetchPosts();
      if (selectedPost && selectedPost.id === postId) {
        const response = await axiosInstance.get(`/blog/${postId}`);
        setSelectedPost(response.data);
      }
    } catch (error) {
      toast.error("Failed to vote");
    }
  };

  const handlePostComment = async () => {
    if (!commentContent.trim()) return;
    try {
      await axiosInstance.post(`/blog/${selectedPost.id}/comments`, { content: commentContent });
      setCommentContent("");
      toast.success("Comment posted!");
      fetchComments(selectedPost.id);
      fetchPosts();
    } catch (error) {
      toast.error("Failed to post comment");
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;
    try {
      await axiosInstance.delete(`/blog/comments/${commentId}`);
      toast.success("Comment deleted");
      fetchComments(selectedPost.id);
      fetchPosts();
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;
    try {
      await axiosInstance.delete(`/blog/${postId}`);
      toast.success("Article deleted");
      setSelectedPost(null);
      fetchPosts();
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  const handleOpenPost = (post) => {
    setSelectedPost(post);
    fetchComments(post.id);
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div data-testid="blog-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
            Useful Articles & Links
          </h1>
          <p className="text-gray-600">Share helpful resources and start discussions</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-post-button" className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-6">
              Share Article
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Share a New Article or Link</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  data-testid="post-title-input"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="content">Content / Link</Label>
                <Textarea
                  id="content"
                  data-testid="post-content-input"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows={12}
                  placeholder="Share an article summary, useful link, or helpful information..."
                  required
                />
              </div>
              <Button data-testid="submit-post-button" type="submit" className="w-full bg-amber-500 hover:bg-amber-600">Publish Article</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6">
        {posts.map((post) => (
          <Card 
            key={post.id} 
            data-testid={`blog-card-${post.id}`}
            className="bg-white/90 backdrop-blur-sm border-amber-100 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => handleOpenPost(post)}
          >
            <CardHeader>
              <CardTitle className="text-2xl" style={{ fontFamily: 'Merriweather, serif' }}>{post.title}</CardTitle>
              <CardDescription className="text-base">
                <div className="flex items-center gap-2">
                  <Avatar className="w-6 h-6">
                    <AvatarFallback>{post.author_name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span>By {post.author_name}</span>
                  <span>•</span>
                  <span>{new Date(post.created_at).toLocaleDateString()}</span>
                </div>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 line-clamp-3 mb-4">{post.content}</p>
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-1"
                  onClick={(e) => handleVote(post.id, e)}
                  data-testid={`vote-post-${post.id}`}
                >
                  <ThumbsUp className="w-4 h-4" />
                  <span>{post.upvotes}</span>
                </Button>
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <MessageSquare className="w-4 h-4" />
                  <span>{post.comment_count} comments</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {posts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No articles yet. Share the first one!</p>
        </div>
      )}

      {/* Article Detail Modal */}
      <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedPost && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                  {selectedPost.title}
                </DialogTitle>
                <div className="flex items-center gap-2 text-gray-600">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback>{selectedPost.author_name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span>By {selectedPost.author_name}</span>
                  <span>•</span>
                  <span>{new Date(selectedPost.created_at).toLocaleDateString()}</span>
                </div>
              </DialogHeader>
              <div className="space-y-4">
                <div className="prose max-w-none">
                  <p className="whitespace-pre-wrap text-gray-700">{selectedPost.content}</p>
                </div>

                <div className="flex items-center gap-4 border-t pt-4">
                  <Button
                    variant="outline"
                    className="flex items-center gap-2"
                    onClick={(e) => handleVote(selectedPost.id, e)}
                    data-testid="vote-post-modal"
                  >
                    <ThumbsUp className="w-4 h-4" />
                    <span>{selectedPost.upvotes} Likes</span>
                  </Button>
                  {(user.id === selectedPost.author_id || user.is_admin) && (
                    <Button 
                      data-testid={`delete-post-${selectedPost.id}`}
                      variant="destructive" 
                      onClick={() => handleDelete(selectedPost.id)}
                      className="rounded-full"
                    >
                      Delete Article
                    </Button>
                  )}
                </div>

                {/* Comments Section */}
                <div className="border-t pt-4">
                  <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
                    Discussion ({comments.length})
                  </h3>

                  <Card className="bg-gray-50 mb-4">
                    <CardContent className="pt-6">
                      <Textarea
                        data-testid="comment-input"
                        placeholder="Join the discussion..."
                        value={commentContent}
                        onChange={(e) => setCommentContent(e.target.value)}
                        rows={3}
                        className="resize-none mb-3"
                      />
                      <Button 
                        data-testid="post-comment-button"
                        onClick={handlePostComment}
                        className="bg-amber-500 hover:bg-amber-600 rounded-full"
                      >
                        Post Comment
                      </Button>
                    </CardContent>
                  </Card>

                  <div className="space-y-3">
                    {comments.map((comment) => (
                      <Card key={comment.id} className="bg-white">
                        <CardContent className="pt-4">
                          <div className="flex gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarImage src={comment.author_picture} />
                              <AvatarFallback>{comment.author_name?.[0]?.toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-sm">{comment.author_name}</span>
                                <span className="text-xs text-gray-500">
                                  {new Date(comment.created_at).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-gray-700 text-sm">{comment.content}</p>
                              {user.id === comment.author_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteComment(comment.id)}
                                  className="text-xs text-red-600 hover:text-red-700 mt-1"
                                  data-testid={`delete-comment-${comment.id}`}
                                >
                                  Delete
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {comments.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      No comments yet. Start the discussion!
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Blog;
